import { extractClickIds, extractUtmParams } from '@ekhodealer/ekho-common/utils';

import type { SdkMarketingAttribution } from '@ekhodealer/ekho-common';

import { resolveIdentity } from './identity';

const STORAGE_KEY = 'ekho_attribution';
const AFFILIATE_REF_STORAGE_KEY = 'ekho_affiliate_ref';

function refFromLocation(): string | null {
  try {
    const value = new URLSearchParams(window.location.search).get('ref')?.trim();
    return value ? value : null;
  } catch {
    return null;
  }
}

/**
 * Last `?ref=` seen on this tab. A new code overwrites. Stored apart from the
 * first-touch UTM snapshot so a later link can replace it, and not cleared
 * with analytics consent: it is the affiliate's code, not a tracking id.
 */
export function readAffiliateCode(): string | null {
  const fromUrl = refFromLocation();
  if (fromUrl) {
    try {
      sessionStorage.setItem(AFFILIATE_REF_STORAGE_KEY, fromUrl);
    } catch {
      // sessionStorage unavailable; this call still returns the URL value.
    }
    return fromUrl;
  }
  try {
    const stored = sessionStorage.getItem(AFFILIATE_REF_STORAGE_KEY)?.trim();
    return stored ? stored : null;
  } catch {
    return null;
  }
}

/**
 * UTM / click ID fields that follow first-touch semantics — captured once per
 * session and persisted in sessionStorage. Identity IDs and Meta/GA IDs are
 * intentionally EXCLUDED from the snapshot: anonymousId/sessionId have their own
 * cookie lifecycles (long-lived + sliding), and fbp/fbc/gaClientId are read live
 * from the dealer's Meta/GA cookies at handoff — all must be re-read rather than
 * shadowed by a stale snapshot value.
 */
type FirstTouchSnapshot = Omit<
  SdkMarketingAttribution,
  'anonymousId' | 'sessionId' | 'fbp' | 'fbc' | 'gaClientId'
>;

/**
 * First-touch snapshot for this page load, held in memory until consent allows
 * a sessionStorage write. Capture and persistence are split: `captureAttribution`
 * fills this, `persistAttribution` writes it once tracking is allowed.
 */
let capturedBaseline: FirstTouchSnapshot | null = null;

function readStored(): FirstTouchSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FirstTouchSnapshot) : null;
  } catch {
    // sessionStorage unavailable (private mode, disabled storage) — non-fatal.
    return null;
  }
}

function parseFromUrl(): FirstTouchSnapshot {
  const params = new URLSearchParams(window.location.search);
  const clickIds = extractClickIds(params);
  const utmParams = extractUtmParams(params);
  return {
    utmSource: utmParams.utmSource ?? null,
    utmMedium: utmParams.utmMedium ?? null,
    utmCampaign: utmParams.utmCampaign ?? null,
    utmContent: utmParams.utmContent ?? null,
    utmTerm: utmParams.utmTerm ?? null,
    utmId: utmParams.utmId ?? null,
    gclid: clickIds.gclid ?? null,
    gbraid: clickIds.gbraid ?? null,
    wbraid: clickIds.wbraid ?? null,
    fbclid: clickIds.fbclid ?? null,
    msclkid: clickIds.msclkid ?? null,
    ttclid: clickIds.ttclid ?? null,
    referrer: document.referrer !== '' ? document.referrer : null,
    landingPage: window.location.href,
    capturedAt: new Date().toISOString(),
  };
}

/**
 * Capture marketing attribution at bootstrap. UTMs and click IDs are first-touch
 * (earliest wins, so an intra-site navigation can't overwrite the landing
 * context). The snapshot stays in memory; `persistAttribution` writes it to
 * sessionStorage once tracking is allowed, so nothing is stored before consent.
 * Identity IDs come from `resolveIdentity` (in-memory), so no cookie is written
 * pre-consent either.
 */
export function captureAttribution(): SdkMarketingAttribution {
  readAffiliateCode();
  const baseline = readStored() ?? parseFromUrl();
  capturedBaseline = baseline;
  const { anonymousId, sessionId } = resolveIdentity();
  return {
    ...baseline,
    // Read live at handoff (withLiveIdentity), not at bootstrap: the dealer's
    // Meta/GA pixels may set these cookies after the loader runs.
    fbp: null,
    fbc: null,
    gaClientId: null,
    anonymousId,
    sessionId,
  };
}

/**
 * Write the first-touch snapshot to sessionStorage. Call once tracking is
 * allowed (or immediately when bypassing). No-op until `captureAttribution` has
 * run.
 */
export function persistAttribution(): void {
  if (!capturedBaseline) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(capturedBaseline));
  } catch {
    // sessionStorage unavailable; attribution still lives in memory.
  }
}

/**
 * Remove the stored attribution on a revoke. The in-memory first-touch snapshot
 * is kept—it is neither written to storage nor sent while consent is withdrawn—so
 * a same-page re-grant can re-persist it via `persistAttribution` instead of
 * losing first-touch UTMs/click IDs for the rest of the page load. A full page
 * load re-captures from scratch regardless.
 */
export function clearAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable; nothing to remove.
  }
}
